import React, {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Row } from "@tanstack/react-table";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import { DataTable } from "ui-components/data-table";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Loading from "ui-components/loading/loading";
import { LoadboardManifestShipment } from "app/data/load-board/models";
import {
  prefetchLoadboardManifestShipments,
  useLoadboardManifestShipments,
} from "hooks/useLoadboard";
import { useDataTableColumnPreferences } from "hooks/useDataTableColumnPreferences";
import {
  getManifestShipmentsColumns,
  getRowCanExpandManifestShipment,
  MANIFEST_SHIPMENTS_COLUMN_OPTIONS,
  MANIFEST_SHIPMENTS_COLUMNS_HIDDEN_BY_DEFAULT,
} from "./manifestShipmentsColumns";
import "./manifestShipmentsSidebar.scss";

const ShipmentDockScanLogNew = React.lazy(
  () => import("features/shipments/shipment-details/shipmentDockScanLogNew")
);

export interface ManifestShipmentsOpenPayload {
  manifestNumber: number | string;
  originLabel?: string;
  destinationLabel?: string;
}

export interface ManifestShipmentsSidebarHandle {
  open: (payload: ManifestShipmentsOpenPayload) => void;
  close: () => void;
}

const formatCount = (value?: number | null): string => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("en", { useGrouping: true, maximumFractionDigits: 0 });
};

const ManifestShipmentsSidebar = forwardRef<ManifestShipmentsSidebarHandle>(
  function ManifestShipmentsSidebar(_, ref) {
    const queryClient = useQueryClient();
    const [payload, setPayload] = useState<ManifestShipmentsOpenPayload | null>(null);
    const [tableReady, setTableReady] = useState(false);
    const [tableHeight, setTableHeight] = useState(400);
    const tableContainerRef = useRef<HTMLDivElement>(null);

    const show = payload != null;
    const manifestNumber = payload?.manifestNumber;
    const originLabel = payload?.originLabel;
    const destinationLabel = payload?.destinationLabel;

    const close = useCallback(() => {
      setPayload(null);
      setTableReady(false);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        open: (next) => {
          void prefetchLoadboardManifestShipments(queryClient, next.manifestNumber);
          setPayload(next);
        },
        close,
      }),
      [close, queryClient]
    );

    useEffect(() => {
      if (!show) {
        setTableReady(false);
        return;
      }
      const frame = window.requestAnimationFrame(() => {
        setTableReady(true);
      });
      return () => window.cancelAnimationFrame(frame);
    }, [show, manifestNumber]);

    useEffect(() => {
      if (!show) return;
      const element = tableContainerRef.current;
      if (!element) return;

      const updateHeight = () => {
        setTableHeight(Math.max(240, element.clientHeight));
      };
      updateHeight();
      const observer = new ResizeObserver(updateHeight);
      observer.observe(element);
      return () => observer.disconnect();
    }, [show, tableReady]);

    const { data, isLoading, isFetching, isError, error } = useLoadboardManifestShipments(
      show ? manifestNumber : null,
      show
    );

    const columnPrefs = useDataTableColumnPreferences({
      instanceKey: "loadboard-manifest-shipments",
      columnOptions: MANIFEST_SHIPMENTS_COLUMN_OPTIONS,
      hiddenByDefault: MANIFEST_SHIPMENTS_COLUMNS_HIDDEN_BY_DEFAULT,
    });

    const columns = useMemo(() => getManifestShipmentsColumns(), []);

    const shipments = data?.shipments ?? [];
    const showSpinner = isLoading || (isFetching && !data);

    const renderSubComponent = useCallback(
      (row: Row<LoadboardManifestShipment>) => (
        <div className="manifest-shipments-sidebar__scan-logs">
          <Suspense
            fallback={
              <div className="manifest-shipments-sidebar__loading">
                <Loading isLoading />
              </div>
            }
          >
            <ShipmentDockScanLogNew invoiceNumber={row.original.probillNumber} />
          </Suspense>
        </div>
      ),
      []
    );

    const routeLabel =
      originLabel || destinationLabel
        ? [originLabel, destinationLabel].filter(Boolean).join(" → ")
        : null;

    return (
      <>
        {show && (
          <button
            type="button"
            className="manifest-shipments-sidebar-backdrop"
            aria-label="Close manifest shipments"
            onClick={close}
          />
        )}
        <SlideOutSidebar
          header={manifestNumber != null ? `Manifest ${manifestNumber}` : "Manifest Shipments"}
          show={show}
          onClose={close}
          size="large"
          spinner={showSpinner}
          className="manifest-shipments-sidebar"
        >
          {routeLabel && (
            <div className="manifest-shipments-sidebar__route">{routeLabel}</div>
          )}

          {isError && !data && (
            <XGSErrorMessage>
              {(error as Error)?.message || "Unable to load manifest shipments."}
            </XGSErrorMessage>
          )}

          {show && !isError && (
            <>
              <div className="manifest-shipments-sidebar__totals" role="group" aria-label="Manifest totals">
                <div className="manifest-shipments-sidebar__totals-item">
                  <span className="manifest-shipments-sidebar__totals-label">PROs</span>
                  <span className="manifest-shipments-sidebar__totals-value">
                    {data ? formatCount(shipments.length) : "—"}
                  </span>
                </div>
                <div className="manifest-shipments-sidebar__totals-item">
                  <span className="manifest-shipments-sidebar__totals-label">Rolls</span>
                  <span className="manifest-shipments-sidebar__totals-value">
                    {data ? formatCount(data.rollCount) : "—"}
                  </span>
                </div>
                <div className="manifest-shipments-sidebar__totals-item">
                  <span className="manifest-shipments-sidebar__totals-label">Pallets</span>
                  <span className="manifest-shipments-sidebar__totals-value">
                    {data ? formatCount(data.palletCount) : "—"}
                  </span>
                </div>
                <div className="manifest-shipments-sidebar__totals-item">
                  <span className="manifest-shipments-sidebar__totals-label">HU</span>
                  <span className="manifest-shipments-sidebar__totals-value">
                    {data ? formatCount(data.huCount) : "—"}
                  </span>
                </div>
                <div className="manifest-shipments-sidebar__totals-item">
                  <span className="manifest-shipments-sidebar__totals-label">Total weight</span>
                  <span className="manifest-shipments-sidebar__totals-value">
                    {data ? `${formatCount(data.totalWeight)} lbs` : "—"}
                  </span>
                </div>
              </div>

              <div ref={tableContainerRef} className="manifest-shipments-sidebar__table">
                {showSpinner || !tableReady ? (
                  <div className="manifest-shipments-sidebar__loading">
                    <Loading isLoading />
                  </div>
                ) : (
                  <DataTable
                    columns={columns}
                    data={shipments}
                    isLoading={false}
                    rowHeight={52}
                    responsive
                    sorting
                    enableMultiSort
                    filters
                    stickyHeaders
                    enableColumnResize
                    enableColumnReorder
                    showColumnSelector
                    columnSelectorOptions={MANIFEST_SHIPMENTS_COLUMN_OPTIONS}
                    enableRowSelection={false}
                    noResultsText="No shipments on this manifest"
                    getRowCanExpand={getRowCanExpandManifestShipment}
                    renderSubComponent={renderSubComponent}
                    autoHeight={false}
                    listHeightOverride={tableHeight ?? 400}
                    {...columnPrefs}
                  />
                )}
              </div>
            </>
          )}
        </SlideOutSidebar>
      </>
    );
  }
);

export default ManifestShipmentsSidebar;
